import { buildWorklist, deriveGreeting, resolveVariant } from '@lume/shared/worklist';
import { Eye } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAgentDashboard } from '../../../hooks/use-agent-dashboard';
import { useCapability } from '../../../hooks/use-capability';
import { useConversations, useConversationCounts } from '../../../hooks/use-conversations';
import {
  useAcceptLowTideSuggestion,
  useDismissLowTideSuggestion,
  useLowTideSuggestions,
} from '../../../hooks/use-low-tide-suggestions';
import { useMySupervisor, useMyTeamPresence } from '../../../hooks/use-presence';
import { useTenantTier } from '../../../hooks/use-tenant-tier';
import { api } from '../../../lib/api-client';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';
import { useAuthStore } from '../../../stores/auth-store';
import { useToastStore } from '../../../stores/toast-store';

import { Corrente } from './Corrente';
import { DayRitualOverlay } from './DayRitualOverlay';
import { ProposalReviewModal } from './ProposalReviewModal';
import { PulsoBand } from './PulsoBand';
import { TeiaRail, type TeiaColleague } from './TeiaRail';
import { useMyPendingApprovals } from './use-my-pending-approvals';
import { useMyProposals } from './use-my-proposals';
import { usePainelLiveRefresh } from './use-painel-live-refresh';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';

import type { ConductionProposalWire } from '../../../lib/api-client';
import type { DualApprovalRequestRaw } from '../../supervisor/types';
import type { FlowAction, FlowItemModel, SuggestionInput } from '@lume/shared/worklist';

import '../painel-2b.css';
import './painel-v3.css';

export function PainelV3(): JSX.Element {
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const fullName = useAuthStore((s) => s.user?.name ?? '');
  const myId = useAuthStore((s) => s.user?.id ?? '');
  const variant = resolveVariant(useTenantTier());
  const canAudit = useCapability('audit-events-read');
  const canDualApproval = useCapability('dual-approval-request-create');
  const dualApprovalEnabled = variant === 'regulated' && canDualApproval;
  // S5 (maré baixa produtiva, Item 405) — gateia o FETCH (não só o render).
  const canLowTideSuggestions = useCapability('proactive-work-suggestions');
  // S7 — "delegar à IA" na sugestão só existe quando a máquina de delegação
  // por etapa está ativa (zero máquina nova — reusa o gate existente).
  const canDelegateSteps = useCapability('ai-step-delegation');
  // Inc 4 — «pedir alçada» = DM ascendente ao supervisor (gesto real só com a cap).
  const canInitiateUpward = useCapability('direct-message-initiate-upward');

  usePainelLiveRefresh();
  const { items: myApprovals } = useMyPendingApprovals(dualApprovalEnabled);
  const { proposals } = useMyProposals(true);
  const [selectedProposal, setSelectedProposal] = useState<ConductionProposalWire | null>(null);
  const { status } = useWebSocket();
  const isLive = !usePrefersReducedMotion();

  const dash = useAgentDashboard();
  const counts = useConversationCounts({ assignedToUserId: 'me' });
  const worklistQ = useConversations({ assignedToUserId: 'me', sort: 'urgency' });
  const presence = useMyTeamPresence();
  const { data: supervisor } = useMySupervisor();
  const lowTideSuggestionsQ = useLowTideSuggestions(canLowTideSuggestions);
  const dismissSuggestion = useDismissLowTideSuggestion();
  const acceptSuggestion = useAcceptLowTideSuggestion();

  const byMode = dash.data?.activeConversations?.byMode ?? { h_a: 0, h_b: 0, h_c: 0 };
  const apptItems = dash.data?.appointmentsToday?.items;
  const queueCount = counts.data?.tabs.awaitingMe ?? 0;
  const suggestionsData = lowTideSuggestionsQ.data;

  // nowMs recomputado a cada render; o "em N min" é point-in-time — o tick ao vivo (WS) vem na Fatia 2.
  const nowMs = Date.now();
  const items: FlowItemModel[] = useMemo(() => {
    const suggestions: SuggestionInput[] = suggestionsData?.suggestions ?? [];
    const built = buildWorklist({
      conversations: worklistQ.data?.data ?? [],
      appointments: apptItems ?? [],
      variant,
      nowMs,
      suggestions,
    });
    // O adapter é agnóstico de capability (sempre emite "delegar" quando há
    // conversa) — o FE filtra por cap `ai-step-delegation` (honest-defer:
    // sem a cap, o gesto simplesmente não renderiza).
    if (canDelegateSteps) return built;
    return built.map((it) =>
      it.kind === 'suggestion'
        ? { ...it, actions: it.actions.filter((a) => a.kind !== 'delegar') }
        : it,
    );
  }, [worklistQ.data, apptItems, variant, nowMs, suggestionsData, canDelegateSteps]);

  const colleagues: TeiaColleague[] = (presence.canRead ? presence.data : [])
    .filter((p) => p.userId !== myId)
    .map((p) => ({
      userId: p.userId,
      name: p.name ?? 'Colega',
      role: '',
      status: p.status,
      load: `${p.currentConversationsCount}/${p.capacity}`,
      free:
        p.currentConversationsCount < p.capacity && (p.status === 'online' || p.status === 'away'),
    }));
  // Inc 4 — «pedir alçada»: inicia um DM ascendente ao supervisor e leva o operador
  // ao thread p/ continuar. Gesto REAL (não abre-e-esquece); a home não tem conversa
  // ativa, então sem âncora. Falha silenciosa com toast honesto (não trava o painel).
  const askAuthority = (supervisorUserId: string): void => {
    void api
      .initiateDirectThread({
        toUserId: supervisorUserId,
        body: 'Preciso de alçada — pode me dar suporte?',
      })
      .then(({ threadId }) => navigate(`/mensagens/${threadId}`))
      .catch(() => push({ variant: 'error', title: 'Não foi possível abrir a mensagem agora.' }));
  };

  const openConversation = (id: string): void => navigate(`/atendimento/conversation/${id}`);

  /** Rótulo curto para o card Teia: tipo legível + tipo de recurso contextual. */
  function dualApprovalLabel(r: DualApprovalRequestRaw): string {
    const typeMap: Record<string, string> = {
      conflict_alert_accept: 'Aceitar alerta',
      override_h_c_adversarial: 'Override adversarial',
      conversation_close_regulated: 'Fechar conversa',
      handoff_critical: 'Handoff crítico',
      custom: 'Pedido custom',
    };
    const label = typeMap[r.requestType] ?? r.requestType;
    return `${label} · ${r.contextResourceType}`;
  }

  const pendingApprovals = myApprovals.map((r) => ({
    id: r.id,
    label: dualApprovalLabel(r),
    slaMinutes:
      r.expiresAt != null
        ? Math.max(0, Math.round((Date.parse(r.expiresAt) - Date.now()) / 60_000))
        : null,
  }));

  const handleAction = (a: FlowAction): void => {
    if (a.kind === 'abrir') {
      openConversation(a.targetId);
      return;
    }
    if (a.kind === 'comunicar') {
      navigate('/agenda');
      return;
    }
    if (a.kind === 'assumir') {
      void api
        .claimConversation(a.targetId)
        .then(() => openConversation(a.targetId))
        .catch(() => push({ variant: 'error', title: 'Não foi possível assumir a conversa' }));
      return;
    }
    if (a.kind === 'retomar') {
      void api
        .takeOver(a.targetId)
        .then(() => openConversation(a.targetId))
        .catch((err: unknown) =>
          // Item 376(a) — em 409 (a IA já devolveu) o backend manda a mensagem
          // clara; mostra ela em vez do genérico (que mascarava o motivo real).
          push({
            variant: 'error',
            title: 'Não foi possível retomar a conversa',
            ...(err instanceof Error ? { description: err.message } : {}),
          }),
        );
      return;
    }
    if (a.kind === 'confirmar') {
      navigate('/agenda');
      return;
    }
    // S5 (maré baixa produtiva) — «fazer agora» com conversa aberta: navega +
    // audita o aceite (fonte autoritativa; fire-and-forget da perspectiva da
    // navegação, que já aconteceu).
    if (a.kind === 'fazer-agora') {
      openConversation(a.targetId);
      if (a.suggestionId !== undefined && a.personId !== undefined) {
        acceptSuggestion.mutate({
          id: a.suggestionId,
          personId: a.personId,
          gesture: 'fazer-agora',
        });
      }
      return;
    }
    // «fazer agora» sem conversa aberta — v1 honesto: navega à ficha da
    // Pessoa (NÃO cria conversa proativa nova; esse fluxo é follow-up).
    if (a.kind === 'ver-pessoa') {
      navigate(`/pessoas/${a.targetId}`);
      if (a.suggestionId !== undefined && a.personId !== undefined) {
        acceptSuggestion.mutate({
          id: a.suggestionId,
          personId: a.personId,
          gesture: 'ver-pessoa',
        });
      }
      return;
    }
    // «delegar à IA» — reusa a conversa (o operador abre o painel de
    // delegação por etapa lá; `?delegate=1` auto-abre o modal existente).
    if (a.kind === 'delegar') {
      navigate(`/atendimento/conversation/${a.targetId}?delegate=1`);
      if (a.suggestionId !== undefined && a.personId !== undefined) {
        acceptSuggestion.mutate({ id: a.suggestionId, personId: a.personId, gesture: 'delegar' });
      }
      return;
    }
    // «agora não» — dispensa (audit grava + suprime 48h); some da lista via
    // invalidate (useDismissLowTideSuggestion).
    if (a.kind === 'dispensar' && a.personId !== undefined) {
      dismissSuggestion.mutate({ id: a.targetId, personId: a.personId });
    }
  };

  const greeting = deriveGreeting(Date.now(), fullName.split(' ')[0] ?? '');

  return (
    <div className={`pv3 pv3--${variant}${isLive ? ' is-live' : ''}`}>
      <div className="pv3__head">
        <div className="pv3__greet">
          <div>
            <h1>{greeting}</h1>
            <div className="sub">
              Seu turno
              {variant === 'regulated' && canAudit && (
                <>
                  {' '}
                  ·{' '}
                  <span className="audit-chip">
                    <Eye size={11} /> auditoria ativa
                  </span>
                </>
              )}
            </div>
          </div>
          {status === 'connected' && (
            <span className="pv3-live">
              <span className="d" /> ao vivo
            </span>
          )}
        </div>
        <DayRitualOverlay />
      </div>

      <div className="pv3__body">
        <div className="pv3__main">
          <PulsoBand variant={variant} queueCount={queueCount} byMode={byMode} />
          <Corrente
            items={items}
            onAction={handleAction}
            onOpen={(it) => {
              if (it.conversationId != null) openConversation(it.conversationId);
            }}
            overduePendencies={dash.data?.overduePendencies?.count}
          />
        </div>
        <TeiaRail
          variant={variant}
          colleagues={colleagues}
          aiConducting={byMode.h_c}
          supervisor={supervisor}
          onAskAuthority={canInitiateUpward ? askAuthority : undefined}
          onPass={() => navigate('/inbox')}
          pendingApprovals={pendingApprovals}
          onReviewApprovals={() => navigate('/supervisor/overrides')}
          proposals={proposals}
          onReviewProposal={(p) => setSelectedProposal(p)}
        />
      </div>

      <ProposalReviewModal proposal={selectedProposal} onClose={() => setSelectedProposal(null)} />
    </div>
  );
}
